<template>
  <div class="product-feed">
    <!-- 分类导航 -->
    <van-tabs v-model:active="activeTab" color="#ee0a24" sticky offset-top="0">
      <van-tab 
        v-for="cat in categories" 
        :key="cat.name" 
        :title="cat.name" 
      />
    </van-tabs>

    <!-- 商品列表 -->
    <van-list
      v-model:loading="loading"
      :finished="finished"
      finished-text="没有更多了"
      v-model:error="error"
      error-text="请求失败，点击重新加载"
      @load="onLoad"
    >
      <div class="product-list">
        <div 
          class="product-item" 
          v-for="item in productList" 
          :key="item.id"
          @click="goToProduct(item)"
        >
          <div class="product-image">
            <van-image
              :src="getImageUrl(item.img_url)"
              fit="cover"
            >
              <template v-slot:loading>
                <van-loading type="spinner" size="20" />
              </template>
              <template v-slot:error>
                <div class="image-error">图片加载失败</div>
              </template>
            </van-image>
            <!-- 平台标签 -->
            <div class="platform-badge" v-if="item.coupon_promotion_link">
              <!--  如果item.gifs>0, 则显示特惠标签，否则显示精品标签 -->
              <van-tag type="danger" size="small" v-if="item.gift > 0">特惠</van-tag>
              <van-tag type="danger" size="small" v-else>精品</van-tag>
            </div>
          </div>
          <div class="product-info">
            <div class="product-title">
              {{ item.title }}
            </div>
            <!-- 优惠券 -->
            <div class="coupon-tag" v-if="Number(item.original_price) > 0 && Number(item.original_price) > Number(item.price)">
              <span class="coupon-label">{{ (Number(item.original_price) - Number(item.price)).toFixed(1) }}元券</span>
            </div>
            <!-- 价格信息 -->
            <div class="price-section">
              <div class="price-info">
                <span class="price-label">到手价</span>
                <span class="current-price">{{ (Number(item.price) - Number(item.gift)).toFixed(1) }}</span>
                <span class="original-price" v-if="Number(item.original_price) > 0">{{ Number(item.original_price).toFixed(1) }}</span>
               <span class="good-rate-value" v-if="item.positive_rating > 0">{{ item.positive_rating }} + 好评</span>
              </div>
            </div>
            <!-- 附加信息 -->
            <div class="extra-info">
              <div class="shop-info">
                <van-icon name="shop-o" size="12" />
                <span class="shop-name">{{ item.shop_name }}</span>
              </div>
            </div>
          </div>
        </div>
      </div>
    </van-list>
  </div>
</template>

<script>
import { ref, watch } from 'vue';
import { getProductList } from '@/api';
import { 
  Tabs, 
  Tab, 
  List, 
  Image as VanImage, 
  Loading,
  Tag,
  Icon,
} from 'vant';

export default {
  name: 'ProductFeed',
  components: {
    [Tabs.name]: Tabs,
    [Tab.name]: Tab,
    [List.name]: List,
    [VanImage.name]: VanImage,
    [Loading.name]: Loading,
    [Tag.name]: Tag,
    [Icon.name]: Icon,
  },
  setup() {
    const activeTab = ref(0);
    const productList = ref([]);
    const loading = ref(false);
    const finished = ref(false);
    const error = ref(false);
    const page = ref(1);
    const pageSize = ref(20);
    
    const categories = [
      { name: '推荐', value: '' },
      { name: '母婴', value: '母婴' },
      { name: '百货', value: '百货' },
      { name: '好券', value: '好券' },
      { name: '热卖', value: '热卖' },
      { name: '美妆', value: '美妆' }
    ];

    const fetchProducts = async () => {
      try {
        error.value = false;
        
        const categoryValue = categories[activeTab.value].value;
        const params = {
          project: 'discount_website',
          page: page.value,
          page_size: pageSize.value
        };
        
        if (categoryValue) {
          params.category = categoryValue;
        }

        const response = await getProductList(params);

        if (response.data.code === 200) {
          const newProducts = response.data.data || [];
          productList.value = [...productList.value, ...newProducts];
          
          const meta = response.data.meta?.pagination;
          if (meta && page.value >= meta.total_page) {
            finished.value = true;
          }
          
          page.value++;
        } else {
          error.value = true;
        }
      } catch (err) {
        console.error('获取商品列表失败:', err);
        error.value = true;
      } finally {
        loading.value = false;
      }
    };

    // 监听分类切换
    watch(activeTab, () => {
      productList.value = [];
      page.value = 1;
      finished.value = false;
      loading.value = true;
      fetchProducts();
    });

    const onLoad = () => {
      fetchProducts();
    };

    const getImageUrl = (url) => {
      if (!url) return '';
      if (url.startsWith('http')) return url;
      return `https://img14.360buyimg.com/n1/${url}`;
    };

    const goToProduct = (item) => {
      const url = item.coupon_promotion_link || item.promotion_link;
      if (url) {
        window.open(url, '_blank');
      }
    };

    return {
      activeTab,
      productList,
      loading,
      finished,
      error,
      categories,
      onLoad,
      getImageUrl,
      goToProduct
    };
  }
};
</script>

<style scoped>
.product-feed {
  background: #f7f8fa;
}

/* 商品列表 - 双列布局优化 */
.product-list {
  padding: 10px;
  display: flex;
  flex-wrap: wrap;
  justify-content: space-between;
  box-sizing: border-box;
}

.product-item {
  width: 48.5%; /* 双列宽度 */
  display: flex;
  flex-direction: column; /* 垂直排列 */
  background: #fff;
  border-radius: 12px;
  margin-bottom: 10px;
  cursor: pointer;
  overflow: hidden; /* 确保图片圆角不溢出 */
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.04); /* 柔和阴影 */
  transition: all 0.3s;
}

.product-item:active {
  transform: scale(0.98);
}

.product-image {
  position: relative;
  width: 100%;
  height: 0;
  padding-bottom: 100%; /* 1:1 正方形图片 */
  margin-right: 0;
  border-radius: 0;
  overflow: hidden;
}

.product-image .van-image {
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
}

.image-error {
  position: absolute;
  top: 0;
  left: 0;
  display: flex;
  align-items: center;
  justify-content: center;
  width: 100%;
  height: 100%;
  background: #f7f8fa;
  color: #969799;
  font-size: 12px;
}

.platform-badge {
  position: absolute;
  top: 6px;
  left: 6px;
  z-index: 1;
}

.product-info {
  flex: 1;
  display: flex;
  flex-direction: column;
  padding: 8px 10px 12px; /* 内容区域内边距 */
}

.product-title {
  font-size: 13px;
  color: #333;
  line-height: 1.4;
  height: 36px; /* 限制两行高度 */
  display: -webkit-box;
  -webkit-box-orient: vertical;
  -webkit-line-clamp: 2;
  line-clamp: 2;
  overflow: hidden;
  margin-bottom: 8px;
  font-weight: 500;
}

.coupon-tag {
  margin-bottom: 8px;
  display: flex;
}

.coupon-label {
  display: inline-flex;
  align-items: center;
  height: 18px;
  background: linear-gradient(90deg, #ff6034, #ee0a24); /* 渐变背景 */
  color: #fff;
  font-size: 10px;
  padding: 0 6px;
  border-radius: 4px;
  font-weight: 500;
  position: relative;
}

/* 给优惠券加一点装饰（可选） */
.coupon-label::before,
.coupon-label::after {
  content: '';
  position: absolute;
  top: 50%;
  width: 4px;
  height: 4px;
  background: #fff;
  border-radius: 50%;
  transform: translateY(-50%);
}
.coupon-label::before { left: -2px; }
.coupon-label::after { right: -2px; }

.price-section {
  margin-bottom: 6px;
  margin-top: auto; /* 将价格推到底部 */
}

.price-info {
  display: flex;
  align-items: baseline;
  flex-wrap: wrap;
}

.price-label {
  font-size: 11px;
  color: #ee0a24;
  margin-right: 2px;
}

.current-price {
  font-size: 18px;
  color: #ee0a24;
  font-weight: 600;
  margin-right: 6px;
  line-height: 1;
}

.current-price::before {
  content: '¥';
  font-size: 12px;
  margin-right: 1px;
}

.original-price {
  font-size: 11px;
  color: #bdc3c7;
  text-decoration: line-through;
}

.original-price::before {
  content: '¥';
}

.good-rate-value {
  margin-left: auto !important;
  font-size: 11px;
  color: #9ca3af;
  margin-right: 6px;
}

.extra-info {
  font-size: 11px;
  color: #9ca3af;
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-top: 4px;
}

.shop-info {
  display: flex;
  align-items: center;
  flex: 1;
  overflow: hidden;
  max-width: 80%;
}

.shop-info .van-icon {
  margin-right: 2px;
  font-size: 11px;
}

.shop-name {
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

/* 分类标签样式 */
:deep(.van-tabs__wrap) {
  background: #fff;
}

:deep(.van-tab) {
  font-size: 15px;
  padding: 0 12px;
}

:deep(.van-tab--active) {
  font-weight: 600;
}
</style>
